import { buildEmbedStyles, resolveEmbed } from './resolve-embed';

/**
 * One `<iframe>`, or nothing at all.
 *
 * Everything an author can set arrives as text and everything this component
 * emits is chosen from a table, and the gap between those two sentences is the
 * component. `resolveEmbed` either recognises the URL as a provider on the
 * allowlist — in which case the `src` is rebuilt from that provider's own
 * hardcoded origin — or it does not, in which case this renders `null`.
 *
 * **Rejection renders nothing, on every surface.** There is no error box and
 * no fallback link, and the composer gets the same empty result the published
 * page would: an author who pastes something unsupported sees at once that it
 * did not take, rather than finding out from a customer. The Modify drawer's
 * URL editor is where they are told why, because it can say so while they are
 * still looking at the field.
 *
 * Nothing here needs the browser. The `src` is in the server's HTML, so the
 * frame starts loading from the markup rather than after hydration.
 */
export function REmbed({
  componentAttributes,
  styleProps,
  className,
}: Readonly<{
  componentAttributes?: { [key: string]: any };
  /** Column styles. Applied to the iframe, which is the only element here. */
  styleProps?: { [key: string]: any };
  /** Host-app classes for this column. */
  className?: string;
}>) {
  const embed = resolveEmbed(componentAttributes);

  if (!embed) {
    return null;
  }

  const { provider } = embed;

  return (
    <iframe
      src={embed.src}
      // Never empty: an untitled frame is announced as "frame" and nothing
      // else, which is worse than a generic name.
      title={embed.title}
      style={{ ...buildEmbedStyles(embed.aspectRatio), ...styleProps }}
      // Fixed per provider, and listed here rather than spread from an
      // attribute bag so that no author-settable value can widen them.
      sandbox={provider.sandbox.join(' ')}
      {...(provider.allow.length > 0 ? { allow: provider.allow.join('; ') } : {})}
      {...(provider.allowFullScreen ? { allowFullScreen: true } : {})}
      // Enough for the providers that gate embedding on the referring domain,
      // and no path or query — which would otherwise leak the customer's URLs
      // to a third party on every page view.
      referrerPolicy="strict-origin-when-cross-origin"
      loading={embed.loading}
      {...(className !== undefined ? { className } : {})}
    />
  );
}
